import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ShieldCheck, Cpu, LogOut, Building2, KeyRound } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { WhipLogo } from "./WhipLogo";

const itemCls = (active) =>
  `flex items-center gap-3 rounded-lg px-3 py-3 text-[15px] font-medium transition-colors ${
    active ? "bg-orange-500/10 text-orange-200 border border-orange-500/30" : "text-white/75 hover:bg-white/5 hover:text-white border border-transparent"
  }`;

export const MobileNav = ({ links, moreLinks, isActive, user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button type="button" aria-label="Open menu" data-testid="nav-mobile-trigger"
          className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg border border-white/15 text-white/80 hover:text-white hover:border-orange-500/40 transition-colors">
          <Menu className="w-5 h-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[86vw] max-w-sm border-l border-white/10 p-0 flex flex-col [&>button]:hidden"
        style={{ background: "rgba(13,13,18,0.98)", backdropFilter: "blur(16px)" }} data-testid="nav-mobile-menu">
        <div className="flex items-center justify-between px-4 h-16 border-b border-white/10">
          <SheetTitle asChild><Link to="/" onClick={close} className="flex items-center"><WhipLogo size={24} /></Link></SheetTitle>
          <button type="button" onClick={close} aria-label="Close menu" data-testid="nav-mobile-close"
            className="flex items-center justify-center w-9 h-9 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {links.map(({ to, testId, label, icon: Icon, match }) => (
            <Link key={to} to={to} onClick={close} data-testid={`mobile-${testId}`} className={itemCls(match())}>
              <Icon className="w-5 h-5 shrink-0" /> {label}
            </Link>
          ))}
          <p className="px-3 pt-4 pb-1 text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-white/35">More</p>
          {moreLinks.map(({ to, testId, label, icon: Icon, blurb }) => (
            <Link key={to} to={to} onClick={close} data-testid={`mobile-${testId}`} className={itemCls(isActive(to))}>
              <Icon className="w-5 h-5 shrink-0" />
              <span className="min-w-0"><span className="block leading-tight">{label}</span><span className="block text-[11px] text-white/40 leading-snug">{blurb}</span></span>
            </Link>
          ))}
        </nav>
        {user && (
          <div className="border-t border-white/10 px-4 py-4 space-y-2" data-testid="nav-mobile-user">
            <p className="text-[13px] font-semibold text-white truncate">{user.name || "n/a"}</p>
            <p className="text-[11px] text-white/45 font-mono truncate">{user.email}</p>
            {user.organization_name && (
              <p className="text-[11px] text-white/55 flex items-center gap-1 truncate"><Building2 className="w-3 h-3 shrink-0" /> {user.organization_name}</p>
            )}
            <span className="inline-flex items-center rounded-full border border-orange-500/40 bg-orange-500/10 px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider text-orange-300">
              WHIP {(user.subscription_tier || "public").toUpperCase()}
            </span>
            <div className="pt-2 space-y-1">
              {user.role === "admin" && (
                <Link to="/admin/users" onClick={close} className={itemCls(false)} data-testid="mobile-nav-admin"><ShieldCheck className="w-4 h-4 text-orange-400" /> User access</Link>
              )}
              {user.role === "admin" && (
                <Link to="/admin/system" onClick={close} className={itemCls(false)} data-testid="mobile-nav-admin-system"><Cpu className="w-4 h-4 text-orange-400" /> System health</Link>
              )}
              {user.hasPassword && (
                <Link to="/account/password" onClick={close} className={itemCls(false)} data-testid="mobile-nav-change-password"><KeyRound className="w-4 h-4 text-white/60" /> Change password</Link>
              )}
              <Link to="/privacy" onClick={close} className={itemCls(false)} data-testid="mobile-nav-privacy"><ShieldCheck className="w-4 h-4 text-white/60" /> Privacy &amp; data</Link>
              <button type="button" onClick={() => { close(); onLogout(); }} className={`${itemCls(false)} w-full`} data-testid="mobile-nav-logout">
                <LogOut className="w-4 h-4 text-white/60" /> Sign out
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
